import { AlertTriangle, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import { Switch } from '../shadComponents/ui/switch';
import { Label } from '../shadComponents/ui/label';
import { Button } from '../shadComponents/ui/button';
import FlowStepInput from './FlowStepInput';
import { Processor } from './components/hooks/useProcessing';

interface FlowStepProps {
    step: Processor;
    stepNumber: number;
    totalSteps: number;
    onDelete: (index: number) => void;
    moveStep: (from: number, to: number) => void;
    updateStep: (newStep: any) => void;
    onLaunchAssetPicker?: (callback: (value: string) => void) => void;
    currentEnvironment: string;
}
export default function FlowStep({
    step,
    stepNumber,
    totalSteps,
    onDelete,
    moveStep,
    updateStep,
    onLaunchAssetPicker,
    currentEnvironment,
}: FlowStepProps) {
    const isLastStep = stepNumber === totalSteps - 1;
    const isFirstStep = stepNumber === 0;

    const isProcessingEnvironmentSupported =
        step.environments?.includes(currentEnvironment) ?? true;

    return (
        <div className="mx-4" key={`step_${stepNumber}`}>
            <div className="Flow-step relative" key={stepNumber}>
                <div className="flex items-start gap-4">
                    <div>
                        <div className="flex">
                            <div className="title capitalize mr-4">
                                {!isProcessingEnvironmentSupported && (
                                    <span title={`This processor may not be supported by the processing environment. Supported in ${step.environments?.join(',') ?? ''}.`}>
                                        <AlertTriangle className="size-4 mr-2 text-yellow-600 inline" />
                                    </span>
                                )}
                                {step.title || step.name}
                            </div>

                            <div className="flex items-center">
                                <Switch
                                    checked={step.saveOutput}
                                    id={`step_${stepNumber}_saveoutput`}
                                    onCheckedChange={(checked) => {
                                        updateStep({
                                            index: stepNumber,
                                            stepObject: {
                                                ...step,
                                                saveOutput: checked,
                                            },
                                        });
                                    }}
                                />
                                <Label
                                    className="text-sm mx-2"
                                    htmlFor={`step_${stepNumber}_saveoutput`}
                                >
                                    Save Output
                                </Label>
                            </div>
                        </div>
                        <div className="flex">
                            <div className="footnote mr-4 my-3">
                                {step.description}
                            </div>
                        </div>

                        <div className="arguments">
                            {Object.keys(step.arguments)
                                .filter((key) => key !== 'in' && key !== 'out')
                                .map((key) => (
                                    <FlowStepInput
                                        key={`${totalSteps}_${stepNumber}_${key}`}
                                        name={key}
                                        stepNumber={stepNumber}
                                        argumentType={
                                            step.argumentTypes
                                                ? step.argumentTypes[key]
                                                : undefined
                                        }
                                        value={step.arguments[key]}
                                        onChange={(value: string | number) => {
                                            step.arguments[key] = value;
                                            updateStep({
                                                index: stepNumber,
                                                stepObject: {
                                                    ...step,
                                                    arguments: {
                                                        ...step.arguments,
                                                        [key]: value,
                                                    },
                                                },
                                            });
                                        }}
                                        onLaunchAssetPicker={
                                            onLaunchAssetPicker
                                        }
                                    />
                                ))}
                        </div>
                    </div>
                    <div className="flex-1" />
                    <div className="flex items-center gap-1">
                        {!isFirstStep && (
                            <Button
                                variant="ghost"
                                size="icon-xs"
                                onClick={() => {
                                    moveStep(stepNumber, stepNumber - 1);
                                }}
                            >
                                <ArrowUp className="size-4" />
                            </Button>
                        )}
                        {!isLastStep && (
                            <Button
                                variant="ghost"
                                size="icon-xs"
                                onClick={() => {
                                    moveStep(stepNumber, stepNumber + 1);
                                }}
                            >
                                <ArrowDown className="size-4" />
                            </Button>
                        )}
                        <Button
                            variant="ghost"
                            size="icon-xs"
                            onClick={() => {
                                onDelete(stepNumber);
                            }}
                        >
                            <Trash2 className="size-4" />
                        </Button>
                    </div>
                </div>
                <div className="flex absolute b-0 r-0"></div>
            </div>
            {stepNumber < totalSteps - 1 && (
                <div className="flow-arrow">
                    <ArrowDown className="size-4 inline" />
                </div>
            )}
        </div>
    );
}
